/* =============================================================
   RatingPage — /rate?token=xxx
   Public page for submitting consultation satisfaction rating
   ============================================================= */
import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { CheckCircle2, Star, Loader2, AlertCircle } from "lucide-react";

export default function RatingPage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") ?? "";
  const preselectedStars = parseInt(params.get("stars") ?? "0", 10);

  const [stars, setStars] = useState(preselectedStars || 0);
  const [hovered, setHovered] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [alreadyRated, setAlreadyRated] = useState(false);

  const { data: ratingInfo, isLoading: infoLoading } = trpc.staffPerformance.getRatingByToken.useQuery(
    { token },
    { enabled: !!token }
  );

  const submitMutation = trpc.staffPerformance.submitRating.useMutation({
    onSuccess: (res) => {
      if (res.success) {
        setSubmitted(true);
      } else if (res.message === "Already rated") {
        setAlreadyRated(true);
      }
    },
  });

  // If stars preselected from email link, auto-submit after a short delay
  useEffect(() => {
    if (preselectedStars >= 1 && preselectedStars <= 5 && token && ratingInfo && !ratingInfo.ratedAt) {
      setStars(preselectedStars);
    }
    if (ratingInfo?.ratedAt) {
      setAlreadyRated(true);
    }
  }, [ratingInfo, preselectedStars, token]);

  const handleSubmit = () => {
    if (!stars || !token) return;
    submitMutation.mutate({ token, stars, feedback: feedback || undefined });
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f4f6fb" }}>
        <div className="text-center p-8">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-gray-500">Буруу холбоос. Мэйлийн линкийг дахин ашиглана уу.</p>
        </div>
      </div>
    );
  }

  if (infoLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f4f6fb" }}>
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!ratingInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f4f6fb" }}>
        <div className="text-center p-8">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-gray-500">Токен олдсонгүй. Мэйлийн линкийг шалгана уу.</p>
        </div>
      </div>
    );
  }

  if (submitted || alreadyRated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f4f6fb" }}>
        <div
          className="text-center p-10 rounded-2xl shadow-xl max-w-sm w-full mx-4"
          style={{ background: "#fff" }}
        >
          <CheckCircle2 className="w-16 h-16 mx-auto mb-4" style={{ color: "#22c55e" }} />
          <h2 className="text-2xl font-bold mb-2" style={{ color: "#1a0a4e" }}>
            {alreadyRated && !submitted ? "Аль хэдийн үнэлсэн байна" : "Баярлалаа!"}
          </h2>
          <p className="text-gray-500 mb-6 text-sm leading-relaxed">
            {alreadyRated && !submitted
              ? "Та энэ зөвлөгөөг аль хэдийн үнэлсэн байна."
              : `Таны ${stars} одны үнэлгээ амжилттай бүртгэгдлээ. Манай ${ratingInfo.staffName} ажилтны ажлыг дэмжсэнд баярлалаа!`}
          </p>
          <a
            href="https://dreamvisamgl.com"
            className="inline-block px-6 py-3 rounded-xl font-bold text-white text-sm"
            style={{ background: "linear-gradient(135deg,#4f46e5,#7c3aed)" }}
          >
            Нүүр хуудас руу буцах
          </a>
        </div>
      </div>
    );
  }

  const displayStars = hovered || stars;

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#f4f6fb" }}>
      <div
        className="w-full max-w-md rounded-2xl shadow-xl overflow-hidden"
        style={{ background: "#fff" }}
      >
        {/* Header */}
        <div
          className="px-8 py-7 text-center"
          style={{ background: "linear-gradient(135deg,#1a0a4e 0%,#2d1b8e 100%)" }}
        >
          <div className="text-2xl font-black text-white tracking-tight">United Dream</div>
          <div className="text-xs text-white/50 tracking-widest uppercase mt-1">Mongolia</div>
        </div>

        {/* Body */}
        <div className="px-8 py-8">
          <h1 className="text-xl font-bold mb-1" style={{ color: "#1a0a4e" }}>
            Сайн байна уу, {ratingInfo.clientName}!
          </h1>
          <p className="text-sm text-gray-500 mb-6 leading-relaxed">
            <strong>{ratingInfo.staffName}</strong> ажилтантай хийсэн зөвлөгөөнийхөө үнэлгээг өгнө үү.
          </p>

          {/* Stars */}
          <div className="mb-6">
            <p className="text-sm font-semibold text-gray-700 mb-3 text-center">
              Зөвлөгөөнд хэр сэтгэл ханамжтай байна вэ?
            </p>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onMouseEnter={() => setHovered(n)}
                  onMouseLeave={() => setHovered(0)}
                  onClick={() => setStars(n)}
                  className="transition-transform hover:scale-110 focus:outline-none"
                  aria-label={`${n} од`}
                >
                  <Star
                    className="w-10 h-10"
                    fill={n <= displayStars ? "#f59e0b" : "none"}
                    stroke={n <= displayStars ? "#f59e0b" : "#d1d5db"}
                    strokeWidth={1.5}
                  />
                </button>
              ))}
            </div>
            {stars > 0 && (
              <p className="text-center text-sm mt-2 font-medium" style={{ color: "#f59e0b" }}>
                {["", "Муу байсан", "Дунд зэрэг", "Хангалттай", "Сайн байсан", "Маш сайн байсан"][stars]}
              </p>
            )}
          </div>

          {/* Optional feedback */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Нэмэлт санал, сэтгэгдэл (заавал биш)
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={3}
              placeholder="Зөвлөгөөний талаар санал бодлоо бичнэ үү..."
              className="w-full px-4 py-3 rounded-xl border text-sm resize-none focus:outline-none focus:ring-2"
              style={{
                border: "1px solid #e5e7eb",
                background: "#fafafa",
                color: "#333",
              }}
            />
          </div>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={!stars || submitMutation.isPending}
            className="w-full py-3.5 rounded-xl font-bold text-white text-sm transition-all disabled:opacity-40"
            style={{ background: "linear-gradient(135deg,#4f46e5,#7c3aed)" }}
          >
            {submitMutation.isPending ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Илгээж байна...
              </span>
            ) : (
              "Үнэлгээ илгээх"
            )}
          </button>

          {/* Contact info */}
          <div
            className="mt-5 p-4 rounded-xl text-sm text-center"
            style={{ background: "#f0f4ff", color: "#555" }}
          >
            Нэмэлт асуух зүйл байвал{" "}
            <a href="mailto:partner@untdream.com" className="font-semibold" style={{ color: "#4f46e5" }}>
              partner@untdream.com
            </a>{" "}
            руу илгээнэ үү.
          </div>
        </div>
      </div>
    </div>
  );
}
